import React, { useState } from 'react';

const DetailedPrograms = () => {
  const [activeLevel, setActiveLevel] = useState('Advanced');

  const programsData = {
    Advanced: [
      {
        title: "AI Research Fellowship",
        badge: "PhD/PostDoc",
        badgeColor: "badge-purple",
        duration: "12 months",
        description: "Advanced research program for AI innovation and breakthrough discoveries",
        features: [
          "Research funding",
          "Mentorship",
          "Publication support",
          "Conference presentations"
        ]
      },
      {
        title: "Neural Network Architecture Design",
        badge: "PhD/PostDoc",
        badgeColor: "badge-purple",
        duration: "18 months",
        description: "Deep dive into designing and optimizing neural network architectures for cutting-edge applications",
        features: [
          "Advanced mathematics",
          "Custom architecture design",
          "Performance optimization",
          "Research publications"
        ]
      },
      {
        title: "AI Ethics & Policy Research",
        badge: "PhD/PostDoc",
        badgeColor: "badge-purple",
        duration: "15 months",
        description: "Interdisciplinary research program focusing on AI governance, ethics, and societal impact",
        features: [
          "Policy development",
          "Ethical framework design",
          "Industry collaboration",
          "Policy publication"
        ]
      }
    ],
    Professional: [
      {
        title: "Graduate AI Specialization",
        badge: "Masters",
        badgeColor: "badge-blue",
        duration: "2 years",
        description: "Comprehensive graduate program covering machine learning, deep learning, and AI ethics",
        features: [
          "Core curriculum",
          "Capstone project",
          "Industry mentorship",
          "Job placement"
        ]
      },
      {
        title: "Machine Learning Engineering",
        badge: "Masters",
        badgeColor: "badge-blue",
        duration: "18 months",
        description: "Professional program focused on deploying ML systems at scale in production environments",
        features: [
          "MLOps practices",
          "System architecture",
          "Cloud deployment",
          "Industry certification"
        ]
      },
      {
        title: "AI Product Management",
        badge: "Masters",
        badgeColor: "badge-blue",
        duration: "15 months",
        description: "Strategic program for leading AI product development and cross-functional AI teams",
        features: [
          "Product strategy",
          "Team leadership",
          "Market analysis",
          "Executive mentorship"
        ]
      }
    ],
    Foundational: [
      {
        title: "Faculty Development Program",
        badge: "Educators",
        badgeColor: "badge-green",
        duration: "6 months",
        description: "Training university faculty to deliver cutting-edge AI education",
        features: [
          "Curriculum design",
          "Teaching methods",
          "Research guidance",
          "Certification"
        ]
      },
      {
        title: "AI Fundamentals Bootcamp",
        badge: "Beginners",
        badgeColor: "badge-green",
        duration: "4 months",
        description: "Intensive foundational program for newcomers to artificial intelligence and machine learning",
        features: [
          "Basic programming",
          "Math foundations",
          "Hands-on projects",
          "Career guidance"
        ]
      },
      {
        title: "Business AI Transformation",
        badge: "Executives",
        badgeColor: "badge-green",
        duration: "3 months",
        description: "Executive program for business leaders looking to integrate AI into their organizations",
        features: [
          "Strategic planning",
          "ROI analysis",
          "Change management",
          "Leadership coaching"
        ]
      }
    ]
  };

  const levels = ['Advanced', 'Professional', 'Foundational'];

  return (
    <>
      <style jsx>{`
        /* Global Styles */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
            'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
            sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        .detailed-programs {
          min-height: 100vh;
          background-color: #000000;
          color: #ffffff;
          padding: 2rem;
        }

        /* Header Styles */
        .header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .header h1 {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .header p {
          color: #9ca3af;
          font-size: 1.25rem;
          max-width: 64rem;
          margin: 0 auto;
          line-height: 1.6;
        }

        /* Level Navigation Styles */
        .level-navigation {
          display: flex;
          justify-content: center;
          margin-bottom: 3rem;
        }

        .level-nav-container {
          display: flex;
          background-color: #111827;
          border-radius: 0.5rem;
          padding: 0.25rem;
        }

        .level-button {
          padding: 0.75rem 2rem;
          border-radius: 0.375rem;
          font-weight: 500;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
          color: #9ca3af;
          background: transparent;
          font-size: 1rem;
        }

        .level-button:hover {
          color: #ffffff;
          background-color: #1f2937;
        }

        .level-button.active {
          background-color: #1f2937;
          color: #ffffff;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        }

        /* Programs Grid Styles */
        .programs-container {
          max-width: 87.5rem;
          margin: 0 auto;
        }

        .programs-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }

        @media (min-width: 768px) {
          .programs-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .programs-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        /* Program Card Styles */
        .program-card {
          background-color: #111827;
          border-radius: 0.75rem;
          padding: 2rem;
          border: 1px solid #374151;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .program-card:hover {
          border-color: #4b5563;
          transform: scale(1.05);
        }

        .program-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), transparent);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .program-card:hover::before {
          opacity: 1;
        }

        /* Program Header Styles */
        .program-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 1.5rem;
          position: relative;
          z-index: 1;
        }

        .program-info {
          flex: 1;
        }

        .program-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.5rem;
          line-height: 1.3;
        }

        .program-duration {
          display: flex;
          align-items: center;
          color: #9ca3af;
          margin-bottom: 1rem;
        }

        .duration-icon {
          width: 1.25rem;
          height: 1.25rem;
          margin-right: 0.5rem;
          stroke: currentColor;
          fill: none;
          stroke-width: 2;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        .program-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.875rem;
          font-weight: 500;
          color: #ffffff;
          white-space: nowrap;
        }

        .badge-purple {
          background-color: #7c3aed;
        }

        .badge-blue {
          background-color: #2563eb;
        }

        .badge-green {
          background-color: #059669;
        }

        /* Program Description Styles */
        .program-description {
          color: #d1d5db;
          margin-bottom: 1.5rem;
          line-height: 1.6;
          position: relative;
          z-index: 1;
        }

        /* Features List Styles */
        .features-list {
          margin-bottom: 2rem;
          position: relative;
          z-index: 1;
        }

        .feature-item {
          display: flex;
          align-items: center;
          margin-bottom: 0.75rem;
        }

        .feature-bullet {
          width: 0.5rem;
          height: 0.5rem;
          background-color: #3b82f6;
          border-radius: 50%;
          margin-right: 0.75rem;
          flex-shrink: 0;
        }

        .feature-text {
          color: #d1d5db;
        }

        /* Apply Button Styles */
        .apply-button {
          width: 100%;
          background-color: #2563eb;
          color: #ffffff;
          font-weight: 500;
          padding: 0.75rem 1.5rem;
          border-radius: 0.5rem;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1rem;
          position: relative;
          z-index: 1;
        }

        .apply-button:hover {
          background-color: #1d4ed8;
        }

        .apply-button:active {
          transform: translateY(1px);
        }

        .button-icon {
          width: 1.25rem;
          height: 1.25rem;
          margin-left: 0.5rem;
          stroke: currentColor;
          fill: none;
          stroke-width: 2;
          stroke-linecap: round;
          stroke-linejoin: round;
          transition: transform 0.3s ease;
        }

        .apply-button:hover .button-icon {
          transform: translateX(0.25rem);
        }

        /* Responsive Design */
        @media (max-width: 767px) {
          .detailed-programs {
            padding: 1rem;
          }
          
          .header h1 {
            font-size: 2.5rem;
          }
          
          .header p {
            font-size: 1.125rem;
          }
          
          .level-button {
            padding: 0.5rem 1rem;
            font-size: 0.875rem;
          }
          
          .program-card {
            padding: 1.5rem;
          }
          
          .program-title {
            font-size: 1.25rem;
          }
          
          .program-header {
            flex-direction: column;
            gap: 1rem;
          }
          
          .program-badge {
            align-self: flex-start;
          }
        }

        @media (max-width: 480px) {
          .header {
            margin-bottom: 2rem;
          }
          
          .header h1 {
            font-size: 2rem;
          }
          
          .level-navigation {
            margin-bottom: 2rem;
          }
          
          .level-nav-container {
            flex-direction: column;
            width: 100%;
            max-width: 300px;
          }
          
          .level-button {
            width: 100%;
            text-align: center;
          }
          
          .programs-grid {
            gap: 1.5rem;
          }
        }

        /* Animation Keyframes */
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .program-card {
          animation: fadeIn 0.5s ease-out;
        }

        /* Focus States for Accessibility */
        .level-button:focus,
        .apply-button:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        /* Custom Scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #111827;
        }

        ::-webkit-scrollbar-thumb {
          background: #374151;
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #4b5563;
        }
      `}</style>

      <div className="detailed-programs">
        {/* Header */}
        <div className="header">
          <h1>Detailed Programs</h1>
          <p>
            Explore our comprehensive curriculum designed for different skill levels and career objectives.
          </p>
        </div>

        {/* Level Navigation */}
        <div className="level-navigation">
          <div className="level-nav-container">
            {levels.map((level) => (
              <button
                key={level}
                onClick={() => setActiveLevel(level)}
                className={`level-button ${activeLevel === level ? 'active' : ''}`}
              >
                {level}
              </button>
            ))}
          </div>
        </div>

        {/* Programs Grid */}
        <div className="programs-container">
          <div className="programs-grid">
            {programsData[activeLevel].map((program, index) => (
              <div key={index} className="program-card">
                {/* Program Header */}
                <div className="program-header">
                  <div className="program-info">
                    <h3 className="program-title">{program.title}</h3>
                    <div className="program-duration">
                      <svg className="duration-icon" viewBox="0 0 24 24">
                        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {program.duration}
                    </div>
                  </div>
                  <span className={`program-badge ${program.badgeColor}`}>
                    {program.badge}
                  </span>
                </div>

                {/* Program Description */}
                <p className="program-description">
                  {program.description}
                </p>

                {/* Features List */}
                <div className="features-list">
                  {program.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="feature-item">
                      <div className="feature-bullet"></div>
                      <span className="feature-text">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default DetailedPrograms;